import * as React from "react";
import {cx} from "emotion";

import "./shipping-rate-modal.scss";

import {cs}            from "@Common/react/chain-services";
import {Load2}         from "@Common/react/load2";
import {UseState}      from "@Common/react/use-state";
import {Radio}         from "@Common/ui-components/heavy/common/design2/radio/radio";
import {formatMoney}   from "@Common/ui-components/language/format-money";
import {ModalService3} from "@Common/ui-components/modal-service-3/modal-service-3";
import {sort}          from "@Common/utils/collections";
import {chain}         from "@Common/utils/fs";

import {CheckShowPricing} from "@Xpresso/routes/common/check-show-pricing";

import {formatServiceLevelName} from "../format-service-level-name";

export const ShippingRateModal = ({shippingItemsCount=undefined, next: rootNext}) => cs(

    ["modal", ({}, next) => ModalService3({
        xdQaName: "Shipping Rate Modal",
        width: 600,
        next,
        rootNext,
    })],

    ["rates", ({modal}, next) => Load2({
        captureException: true,
        fetch: modal.params.fetch,
        next,
    })],
    ["selecting", (_, next) => UseState({next})],
    ["selectError", (_, next) => UseState({next})],
    ({rates, modal, selecting, selectError}) => (
        <div className="shipping-rate-modal-34r">
            <div className="title" xd-qa-type="label" xd-qa-name="Shipping Rate Description">
                Please select a shipping service level
            </div>

            {!!modal.params.disclaimer && (
                <div {...{
                    className: "disclaimer",

                    "xd-qa-type": "text",
                    "xd-qa-name": "Disclaimer",
                }}>
                    {modal.params.disclaimer}
                </div>
            )}

            {rates.value == null && !rates.exception && (
                <div className="data-loading" xd-qa-type="label" xd-qa-name="Loading">
                    Loading applicable shipping rates...
                </div>
            )}

            {selecting.value ? (
                <div className="message selecting">
                    Selecting...
                </div>
            ) : selectError.value ? (
                <div className="message error" xd-qa-type="label" xd-qa-name="Shipping Rates Error">
                    {selectError.value.error ?? "Failed to change shipping rate"}
                </div>
            ) : (
                <div className="message">
                    &nbsp;
                </div>
            )}

            {rates.exception ? (
                <div className="message error" xd-qa-type="label" xd-qa-name="Shipping Rates Error">
                    {rates.exception.error_code}
                </div>
            ) : (
                rates.value && chain(
                    Object.keys(rates.value),
                    (_) => sort(_, (rate: string) => -rates.value[rate].price),
                    (_) => _.map((rate, i) => {
                        const selected1 = rate === modal.params.selected;
                        const selecting1 = selecting.value === rate;
                        const isNotAvailableForAllItems = shippingItemsCount && shippingItemsCount !== rates.value[rate].item_details?.length;
                        return (
                            <div
                                key={i}
                                className={cx("rate", {selected: selected1, selecting: selecting1, serviceLevelError: isNotAvailableForAllItems})}
                                onClick={async () => {
                                    if (selected1 || selecting.value || isNotAvailableForAllItems) {
                                        return;
                                    } else {
                                        try {
                                            selecting.onChange(rate);
                                            await modal.params.onSelect(rate);
                                            modal.close();
                                        } catch (error) {
                                            selectError.onChange({error});
                                        } finally {
                                            selecting.onChange(null);
                                        }
                                    }
                                }}
                            >
                                <div className="checkbox">
                                    {Radio({
                                        xdQaName: formatServiceLevelName(rate),
                                        selected: (selected1 && !isNotAvailableForAllItems),
                                        disabled: isNotAvailableForAllItems,
                                    })}
                                </div>

                                <div className="text">
                                    <div
                                        xd-qa-type="label"
                                        xd-qa-name={formatServiceLevelName(rate)}
                                        className="name"
                                    >
                                        {formatServiceLevelName(rate)}
                                    </div>
                                    <div
                                        xd-qa-type="label"
                                        xd-qa-name={"Delivery " + formatServiceLevelName(rate) }
                                        className={cx("delivery", {serviceLevelDeliveryError: isNotAvailableForAllItems})}
                                    >
                                        {isNotAvailableForAllItems ? (
                                            'This service level is not available for all orders in the cart. Please choose a different service level or select "Split Shipping" to apply separate selections for your orders.'
                                        ): (
                                            rates.value[rate].delivery_message
                                        )}
                                    </div>
                                </div>

                                {cs(
                                    ({}, next) => !isNotAvailableForAllItems && next(),
                                    ({}, next) => CheckShowPricing({next}),
                                    () => {
                                        const r = rates.value[rate];
                                        return (
                                            <div
                                                className="price"
                                            >
                                                <div
                                                    xd-qa-type="label"
                                                    xd-qa-name={"Shipping Price " + formatServiceLevelName(rate) }
                                                    className="line1"
                                                >
                                                    {formatMoney(+r.price)}
                                                </div>

                                                {r.actual_price != null && (
                                                    <div
                                                        xd-qa-type="label"
                                                        xd-qa-name={"Actual Shipping Price " + formatServiceLevelName(rate) }
                                                        className="actual-price"
                                                    >
                                                        Actual price:{" "}
                                                        <b>
                                                            {formatMoney(+r.actual_price)}
                                                        </b>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                )}
                            </div>
                        );
                    })
                )
            )}
        </div>
    ),
);
